import React, { useMemo } from "react";
import type { LLMModel } from "./types";

type Props = {
  model?: LLMModel | null;
  t: (key: string, options?: { defaultValue?: string }) => string;
};

const ALLOWED_KEYS = new Set([
  "temperature",
  "max_tokens",
  "embedding_dimension",
  "extra_config.context_length",
  "extra_config.max_output_tokens",
  "extra_config.max_input_tokens",
  "extra_config.top_p",
  "extra_config.max_image_size",
  "extra_config.max_images_per_request",
  "extra_config.max_input_characters",
  "extra_config.max_audio_duration_seconds",
  "extra_config.max_audio_size",
  "extra_config.supported_audio_format",
  "extra_config.output_formats",
]);

const formatLabel = (key: string): string =>
  key
    .split(".")
    .pop()!
    .replace(/_([a-z])/g, (_, character: string) => character.toUpperCase());

const normalizeConfig = (data: LLMModel): Record<string, unknown> => {
  const { extra_config = {}, ...rest } = data;
  return {
    ...rest,
    ...Object.fromEntries(
      Object.entries(extra_config).map(([key, value]) => [
        `extra_config.${key}`,
        value,
      ]),
    ),
  };
};

const isValidValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") return false;
  return !Array.isArray(value) || value.length > 0;
};

const formatValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

type ConfigItemProps = {
  label: string;
  value: unknown;
};

const ConfigItem: React.FC<ConfigItemProps> = ({ label, value }) => (
  <div className="rounded-[10px] border border-border bg-card px-3 py-2">
    <p className="break-words text-sm font-normal text-foreground">
      {formatValue(value)}
    </p>
    <p className="text-xs font-normal text-muted-foreground">{label}</p>
  </div>
);

const AICatalogDetailConfigurationsComp: React.FC<Props> = ({ model, t }) => {
  const filtered = useMemo(() => {
    if (!model) return [];
    const normalized = normalizeConfig(model);
    return Object.entries(normalized).filter(
      ([key, value]) => ALLOWED_KEYS.has(key) && isValidValue(value),
    );
  }, [model]);

  if (!filtered.length) return null;

  return (
    <section className="space-y-1" aria-label={t("aiCatalog.configurations")}>
      <p className="text-xs font-medium text-muted-foreground">
        {t("aiCatalog.configurations")}
      </p>
      <div className="space-y-2">
        {filtered.map(([key, value]) => {
          const formattedKey = formatLabel(key);
          return (
            <ConfigItem
              key={key}
              value={value}
              label={t(`modelRegistration.${formattedKey}`, {
                defaultValue: formattedKey,
              })}
            />
          );
        })}
      </div>
    </section>
  );
};

export default AICatalogDetailConfigurationsComp;
